import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

export default function Workshops() {
  const [currentImage, setCurrentImage] = useState(0);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", workshop: "" });

  const images = [
    "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop"
  ];


  const schedules = [
    {
      title: "AI in Digital Marketing",
      date: "Every Saturday",
      time: "10:00 AM - 4:00 PM",
      agenda: [
        "Introduction to AI & Marketing Trends",
        "ChatGPT for Content Creation",
        "AI-Powered Analytics & Insights",
        "Building Automated Campaigns",
        "Hands-on Mini Campaign Project",
        "Q&A and Career Guidance"
      ]
    },
    {
      title: "Hands-on PCB Prototyping",
      date: "Every Sunday",
      time: "2:00 PM - 6:00 PM",
      agenda: [
        "Circuit Design Fundamentals",
        "KiCad Schematic Creation",
        "PCB Layout & Routing",
        "Component Selection",
        "Soldering & Assembly",
        "Testing & Troubleshooting"
      ]
    },
    {
      title: "Machine Learning Projects",
      date: "Bi-weekly Wednesdays",
      time: "6:00 PM - 9:00 PM",
      agenda: [
        "Python & Data Science Basics",
        "Supervised Learning Models",
        "Neural Networks Introduction",
        "Real-world ML Applications",
        "Capstone Project Planning",
        "Deployment & Best Practices"
      ]
    }
  ];


  const handleBookingSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for your interest! We'll contact you soon with booking details.");
    setBookingForm({ name: "", email: "", workshop: "" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      <SiteHeader />

      {/* Hero Section */}
      <section className="relative overflow-clip pt-12 sm:pt-16">
        <div className="absolute -inset-x-6 -top-24 h-80 blur-3xl opacity-30 bg-[radial-gradient(60%_60%_at_50%_40%,theme(colors.violet.500),transparent_60%)]" />
        <div className="mx-auto max-w-7xl px-4 pt-10 sm:pt-14 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
              3 Month Workshops at
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">NeuraCore AI Institute</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
              Intensive 3-month programs combining theory with hands-on projects. Master industry skills with expert mentorship, build portfolio-worthy projects, and launch your career in tech.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#booking" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition text-sm sm:text-base">
                Book Your Spot
              </a>
              <a href="#schedule" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold border border-slate-300 hover:border-slate-400 text-sm sm:text-base">
                View Schedules
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Workshop Use Cases</h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base">Discover how our workshops apply to real-world scenarios</p>
          </div>
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <UseCaseCard
              title="AI in Digital Marketing"
              description="Learn how artificial intelligence is transforming marketing strategies, from personalized campaigns to predictive analytics."
              applications={["Automated content creation", "Customer behavior prediction", "Dynamic pricing optimization", "Chatbot development"]}
              icon="🤖"
            />
            <UseCaseCard
              title="Hardware Design & Prototyping"
              description="Master the art of bringing electronic ideas to life through hands-on PCB design and prototyping."
              applications={["IoT device development", "Wearable technology", "Smart home systems", "Industrial automation"]}
              icon="🔧"
            />
            <UseCaseCard
              title="Machine Learning Projects"
              description="Build practical ML models for real-world applications across various industries."
              applications={["Image recognition systems", "Recommendation engines", "Predictive maintenance", "Natural language processing"]}
              icon="🧠"
            />
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-tr from-indigo-50 via-white to-violet-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Workshop Gallery</h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base">See our participants in action</p>
          </div>
          <div className="relative">
            <motion.div
              key={currentImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="aspect-[16/10] rounded-3xl overflow-hidden shadow-xl"
            >
              <img
                src={images[currentImage]}
                alt={`Workshop ${currentImage + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
            <div className="flex justify-center mt-6 gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-3 h-3 rounded-full transition ${
                    index === currentImage ? 'bg-indigo-600' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setCurrentImage((prev) => (prev - 1 + images.length) % images.length)}
                className="px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentImage((prev) => (prev + 1) % images.length)}
                className="px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* Schedules */}
      <section id="schedule" className="py-12 sm:py-16 bg-gradient-to-tr from-indigo-50 via-white to-violet-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Workshop Schedules</h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base">Detailed agendas for each workshop</p>
          </div>
          <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {schedules.map((schedule, index) => (
              <motion.div
                key={schedule.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.03, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-6 shadow-sm hover:shadow-xl transition-shadow"
              >
                <h3 className="text-xl font-bold mb-2">{schedule.title}</h3>
                <div className="text-sm text-slate-600 mb-4">
                  <p><strong>Date:</strong> {schedule.date}</p>
                  <p><strong>Time:</strong> {schedule.time}</p>
                </div>
                <h4 className="font-semibold mb-3">Agenda:</h4>
                <ul className="space-y-2">
                  {schedule.agenda.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Book Your Workshop</h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base">Secure your spot in our 3-month workshops</p>
          </div>
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleBookingSubmit}
            className="bg-slate-50 rounded-3xl p-6 sm:p-8 shadow-sm"
          >
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-semibold mb-2">Full Name</label>
                <input
                  type="text"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Email Address</label>
                <input
                  type="email"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-semibold mb-2">Preferred Workshop</label>
              <select
                value={bookingForm.workshop}
                onChange={(e) => setBookingForm({...bookingForm, workshop: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                required
              >
                <option value="">Select a workshop</option>
                <option value="ai-marketing">AI in Digital Marketing</option>
                <option value="pcb-prototyping">Hands-on PCB Prototyping</option>
                <option value="ml-projects">Machine Learning Projects</option>
              </select>
            </div>
            <div className="mt-8 text-center">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-2xl px-8 py-3 font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
              >
                Submit Booking
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      <SiteFooter />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/1234567890" // Replace with actual WhatsApp number
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-colors duration-200 z-50"
        aria-label="Contact us on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-6 h-6"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      </a>
    </div>
  );
}

function Logo({ className = "h-9 w-auto" }) {
  return (
    <img src="/logo.png" alt="NCAI" className={className} />
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/#programs" className="hover:text-indigo-600">Programs</Link>
            <Link to="/workshops" className="hover:text-indigo-600">Workshops</Link>
            <Link to="/#flex" className="hover:text-indigo-600">Flexible Learning</Link>
            <Link to="/#outcomes" className="hover:text-indigo-600">Outcomes</Link>
            <Link to="/#faq" className="hover:text-indigo-600">FAQ</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/apply"
              className="rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function UseCaseCard({ title, description, applications, icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-slate-50 rounded-3xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-shadow"
    >
      <div className="text-3xl sm:text-4xl mb-4">{icon}</div>
      <h3 className="text-lg sm:text-xl font-bold mb-3">{title}</h3>
      <p className="text-slate-600 mb-4 text-sm sm:text-base">{description}</p>
      <h4 className="font-semibold mb-2 text-sm sm:text-base">Key Applications:</h4>
      <ul className="space-y-1">
        {applications.map((app, index) => (
          <li key={index} className="flex gap-2 text-sm">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-600 flex-shrink-0" />
            {app}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8 lg:grid-cols-4 md:grid-cols-2">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <Logo />
              <span className="text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
            </Link>
            <p className="text-sm text-slate-600 mb-4">
              Empowering careers in Digital Marketing, AI & Machine Learning, and Hardware Design & Manufacturing.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.749.097.118.112.221.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.747-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001.012.017z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="#programs" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Programs</a></li>
              <li><Link to="/workshops" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Workshops</Link></li>
              <li><a href="#flex" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Flexible Learning</a></li>
              <li><a href="#outcomes" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Outcomes</a></li>
              <li><a href="#faq" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">FAQ</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Us</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="text-slate-400">📧</span>
                <a href="mailto:info@ncai.in" className="hover:text-indigo-600 transition-colors">info@ncai.in</a>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-slate-400">📞</span>
                <a href="tel:+917356011039" className="hover:text-indigo-600 transition-colors">7356011039</a>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">📍</span>
                <span>Opposite to John Cox Engineer College,<br />Kannanmoola, Trivandrum</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Stay Updated</h3>
            <p className="text-sm text-slate-600 mb-4">Subscribe to our newsletter for the latest updates and insights.</p>
            <form className="space-y-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="submit"
                className="w-full px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 rounded-lg hover:shadow-md transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} NeuraCore AI Institute (NCAI). All rights reserved.</p>
            <div className="flex gap-6 text-sm text-slate-500">
              <Link to="/privacy-policy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-indigo-600 transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}